import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Button from "@/components/Button";
import Marquee from "@/components/Marquee";
import { ABOUT_PORTRAIT } from "@/data/site";
import { scaleImg } from "@/lib/motion";
import Seo from "@/components/Seo";

const VALUES = [
  { t: "Cercanía", d: "Trabajamos contigo, no para ti. Tu visión es el punto de partida de todo." },
  { t: "Detalle", d: "Cuidamos cada pixel y cada palabra. Lo pequeño hace lo memorable." },
  { t: "Emoción", d: "Diseñamos para que la gente sienta antes de pensar." },
];

const SobreMi = () => {
  return (
    <div data-testid="sobre-mi-page">
      <Seo
        title="Sobre mí"
        description="Conoce la historia y filosofía detrás de Lu Design: un estudio creativo que diseña con cercanía, detalle y emoción."
        path="/sobre-mi"
      />
      <PageHero
        overline="Sobre mí"
        titleLines={["Detrás de", "cada historia."]}
        testId="sobre-mi-hero"
      />

      {/* Editorial: retrato + texto */}
      <section className="relative z-10 bg-ivory py-8 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid items-start gap-12 md:gap-20 md:grid-cols-2">
            {/* Retrato con bloque desplazado */}
            <div className="relative md:sticky md:top-32">
              <div className="absolute -left-5 -top-5 h-full w-full bg-powder/50" />
              <div className="relative overflow-hidden">
                <motion.img
                  variants={scaleImg}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.3 }}
                  src={ABOUT_PORTRAIT}
                  alt="Fundadora de Lu Design"
                  data-testid="about-portrait"
                  loading="lazy"
                  decoding="async"
                  className="h-[32rem] w-full object-cover md:h-[38rem]"
                />
              </div>
            </div>

            {/* Historia */}
            <div className="md:pt-8">
              <Reveal>
                <p className="font-serif text-3xl md:text-4xl font-light leading-snug text-plum">
                  Hola, soy Lu. Creé este estudio para diseñar cosas que{" "}
                  <span className="italic">emocionan</span>.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-8 font-sans font-light text-base md:text-lg leading-relaxed text-charcoal/75">
                  Lu Design nació de una convicción sencilla: el diseño es una forma de cuidar.
                  De poner atención, de escuchar y de traducir lo que una marca quiere decir en algo
                  que se pueda ver, tocar y sentir. Cada proyecto es una conversación, y cada
                  entrega, una historia que empieza a caminar sola.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 font-sans font-light text-base md:text-lg leading-relaxed text-charcoal/75">
                  Combino sensibilidad estética con estrategia para crear identidades, sitios web,
                  invitaciones y fotografía que no solo se ven bonitas: conectan. Creo en el espacio
                  en blanco, en los detalles delicados y en el poder de una buena historia bien contada.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <blockquote className="mt-10 border-l-2 border-rosegold pl-6 font-serif italic text-2xl md:text-3xl text-plum">
                  “Diseño que conecta e inspira.”
                </blockquote>
              </Reveal>

              <Reveal delay={0.4}>
                <div className="mt-12">
                  <Button to="/contacto" testId="sobre-mi-cta">
                    Trabajemos juntas
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Filosofía / valores */}
      <section className="relative z-10 bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="mb-14 text-xs uppercase tracking-[0.3em] text-rosegold">Filosofía</p>
          </Reveal>
          <div className="grid gap-12 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.12}>
                <div className="border-t border-rosegold/30 pt-6">
                  <h3 className="font-serif text-3xl text-plum">{v.t}</h3>
                  <p className="mt-4 font-sans font-light text-sm leading-relaxed text-charcoal/70">
                    {v.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-plum py-12 md:py-16 text-ivory">
        <Marquee className="text-4xl md:text-6xl" text="Diseño · Emoción · Conexión" />
      </section>
    </div>
  );
};

export default SobreMi;
